// StrategyStoryDialog — the in-app presentation view.
//
// Selection happens BEFORE presenting (the dashboard's scenario checkboxes,
// or the full comparison set) — there is no picker in here. The dialog
// receives [baseline, ...chosen strategies].
//
// ONE strategy chosen → the step-by-step Build story: Where You Are, then
// one step per lever (Roth, gains, alt…), ending at the payoff. The middle
// steps' numbers come from the SAME calculate-tax edge function every save
// uses (read-only, sandbox-allowlisted); the final step always uses the
// scenario's STORED results so the story can never disagree with the table.
// SEVERAL chosen → side-by-side summary cards, each with an expand control
// that opens that plan's step story.
//
// Nothing here writes anything, and no engine code is imported — the engine
// is only ever reached over the network via calculateTaxServer.

import { useEffect, useMemo, useState } from "react";
import type { TaxScenario } from "@/tax/types";
import { deriveBuildStory, deriveStorySteps, type StoryCard, type ComputedStep } from "@/lib/buildStory";
import { calculateTaxServer } from "@/tax/taxApi";
import { CYA_TEXT } from "@/lib/disclaimer";
import { formatCurrency, formatPercent } from "@/tax/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowDown, ArrowLeft, Loader2, Maximize2, Printer } from "lucide-react";
import { toast } from "sonner";

interface StrategyStoryDialogProps {
  /** Baseline first, then the strategies chosen for this presentation. */
  scenarios: TaxScenario[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function MovePill({ label, amount, direction }: { label: string; amount: number; direction: "up" | "down" }) {
  const warm = direction === "up";
  return (
    <div
      className={`rounded-xl px-4 py-3 text-white text-center shadow ${
        warm
          ? "story-pill-warm bg-gradient-to-br from-orange-500 to-red-600"
          : "story-pill-cool bg-gradient-to-br from-sky-500 to-blue-600"
      }`}
    >
      <div className="text-base font-extrabold tabular-nums">
        {label} {warm ? "+" : "−"}
        {formatCurrency(amount)}
      </div>
    </div>
  );
}

function TaxPanel({ card, maxTax }: { card: StoryCard; maxTax: number }) {
  const barPct = Math.max(6, Math.round((card.taxThisYear / maxTax) * 100));
  return (
    <div className="rounded-xl border bg-muted/30 p-4">
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
        Tax this year
      </div>
      <div className="mt-1.5 flex items-center gap-3">
        <div
          className={`h-4 rounded ${card.isBaseline ? "bg-slate-400" : card.taxDeltaVsBase !== null && card.taxDeltaVsBase < 0 ? "bg-blue-500" : "bg-slate-700"}`}
          style={{ width: `${barPct}%`, minWidth: 8 }}
        />
        <div className="whitespace-nowrap font-extrabold tabular-nums">
          {formatCurrency(card.taxThisYear)}
        </div>
      </div>
      {card.isBaseline ? (
        <div className="mt-1.5 text-xs font-semibold tabular-nums text-muted-foreground">
          {formatPercent(card.effectiveRate)} effective rate
        </div>
      ) : (
        card.taxDeltaVsBase !== null &&
        Math.abs(card.taxDeltaVsBase) >= 0.5 && (
          <div
            className={`mt-1.5 text-xs font-bold tabular-nums ${card.taxDeltaVsBase > 0 ? "text-amber-700" : "text-success"}`}
          >
            {card.taxDeltaVsBase < 0
              ? `saves ${formatCurrency(Math.abs(card.taxDeltaVsBase))} vs doing nothing`
              : `+${formatCurrency(card.taxDeltaVsBase)} vs doing nothing`}
          </div>
        )
      )}
      {card.notes.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-dashed pt-2 text-xs text-muted-foreground">
          {card.notes.map((n) => (
            <p key={n}>{n}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function StatLine({ card }: { card: StoryCard }) {
  return (
    <div className="mt-3 flex flex-wrap justify-center gap-6 border-t border-dashed pt-3 text-center">
      <div>
        <div className="text-sm font-extrabold tabular-nums">{formatCurrency(card.taxableIncome)}</div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">taxable income</div>
        {card.taxableIncomeDeltaVsBase !== null && (
          <div className="text-[10px] font-semibold tabular-nums text-muted-foreground">
            {card.taxableIncomeDeltaVsBase >= 0 ? "+" : "−"}
            {formatCurrency(Math.abs(card.taxableIncomeDeltaVsBase))} vs baseline
          </div>
        )}
      </div>
      <div>
        <div className="text-sm font-extrabold tabular-nums">{formatPercent(card.effectiveRate)}</div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">effective rate</div>
        {card.effectiveRateDeltaVsBase !== null && (
          <div className="text-[10px] font-semibold tabular-nums text-muted-foreground">
            {card.effectiveRateDeltaVsBase >= 0 ? "+" : "−"}
            {formatPercent(Math.abs(card.effectiveRateDeltaVsBase))} vs baseline
          </div>
        )}
      </div>
      <div>
        <div className="text-sm font-extrabold tabular-nums">{formatCurrency(card.deductionUsed)}</div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">deduction used</div>
      </div>
    </div>
  );
}

/** Green payoff box — condition-gated detail from the scenario's own results. */
function PayoffBox({ fvEconDiff, scenario, growthYears }: { fvEconDiff: number | null; scenario: TaxScenario; growthYears: number }) {
  if (fvEconDiff === null) return null;
  const r = scenario.results;
  const parts: string[] = [];
  if (r.rothFutureValue > 0) parts.push(`${formatCurrency(r.rothFutureValue)} tax-free Roth by Year ${growthYears}`);
  if (r.estimatedFutureTaxAvoided > 0) parts.push(`${formatCurrency(r.estimatedFutureTaxAvoided)} future taxes avoided`);
  if (r.hpeNetCashReturned > 0)
    parts.push(
      `${formatCurrency(r.hpeNetCashReturned)} alt investment cash back (hypothetical)`,
    );
  return (
    <div className="mt-3 rounded-xl border border-green-300 bg-green-50 p-3 text-center dark:border-green-800 dark:bg-green-950/40">
      <div className={`text-xl font-extrabold tabular-nums ${fvEconDiff >= 0 ? "text-success" : "text-destructive"}`}>
        {fvEconDiff >= 0 ? "+" : ""}
        {formatCurrency(fvEconDiff)}
      </div>
      <div className="text-[11px] font-medium text-green-900 dark:text-green-200">
        est. future-value economic difference vs doing nothing
      </div>
      {parts.length > 0 && (
        <div className="mt-1.5 border-t border-dashed border-green-300 pt-1.5 text-[10px] tabular-nums text-green-900 dark:border-green-800 dark:text-green-200">
          {parts.join(" · ")}
        </div>
      )}
    </div>
  );
}

export default function StrategyStoryDialog({ scenarios, open, onOpenChange }: StrategyStoryDialogProps) {
  const baseline = scenarios[0];
  const story = useMemo(() => deriveBuildStory(scenarios), [scenarios]);
  const byId = useMemo(() => new Map(scenarios.map((s) => [s.id, s] as const)), [scenarios]);
  const baseCard = story.cards[0];
  const planCards = story.cards.slice(1);
  const single = planCards.length === 1;

  // Step-story state. Single selection auto-opens its story; from the
  // side-by-side view each card has an expand control into its story.
  const [storyId, setStoryId] = useState<string | null>(null);
  const [stepsCache, setStepsCache] = useState<Record<string, ComputedStep[]>>({});
  const [loadingStory, setLoadingStory] = useState(false);

  const openStory = (id: string) => {
    setStoryId(id);
    if (stepsCache[id]) return;
    const scenario = byId.get(id);
    if (!scenario || !baseline) return;
    setLoadingStory(true);
    deriveStorySteps(baseline, scenario, calculateTaxServer)
      .then((steps) => setStepsCache((prev) => ({ ...prev, [id]: steps })))
      .catch(() => {
        toast.error("Couldn’t build the step story", { description: "Showing the summary instead." });
        setStoryId(null);
      })
      .finally(() => setLoadingStory(false));
  };

  // Single selection auto-opens its story; closing the dialog resets it.
  const autoStoryId = single ? planCards[0]?.scenarioId ?? null : null;
  useEffect(() => {
    if (open && autoStoryId) openStory(autoStoryId);
    if (!open) setStoryId(null);
    // openStory is stable for a given scenarios prop; deps intentionally minimal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStoryId]);

  // Browser print (→ "Save as PDF"), guaranteed one page, WYSIWYG per view:
  //   · step story (single strategy or expanded card) → letter PORTRAIT
  //   · side-by-side comparison → letter LANDSCAPE — the cards keep their
  //     columns, and a portrait page is too narrow to hold 2–3 abreast.
  // The print CSS clamps <html>/<body> to exactly one page with overflow
  // hidden; this handler measures the content AT THE PRINTED WIDTH and
  // injects the transform scale that fits it inside. transform, not zoom —
  // Chromium's paginator ignores zoom when slicing pages, which is how
  // earlier printouts spilled to 3 pages. @page{margin:0} suppresses the
  // browser's own date/URL header & footer; visual margins are padding
  // inside the scaled area. The scroll region also gets a min-height so the
  // disclaimer footer sits at the bottom edge of the page, like on screen.
  const handlePrint = () => {
    const sideBySide = !single && !storyId;
    const pageW = sideBySide ? 1056 : 816; // US Letter @ 96dpi
    const pageH = sideBySide ? 816 : 1056;
    const PAD_X = 106; // print padding: 14mm left + 14mm right
    const PAD_Y = 84; // 12mm top + 10mm bottom

    const area = document.getElementById("story-print-area");
    const scrollEl = area?.querySelector<HTMLElement>(".story-scroll") ?? null;

    // Header + footer chrome (the scroll region's siblings), measured for
    // real instead of guessed. The absolutely-positioned close X is a
    // button and prints hidden, so buttons are skipped.
    let chrome = PAD_Y;
    if (area && scrollEl) {
      for (const child of Array.from(area.children)) {
        if (child === scrollEl || !(child instanceof HTMLElement) || child.tagName === "BUTTON") continue;
        chrome += child.offsetHeight + 16; // + flex gap
      }
    }

    // Scaling lays the content out WIDER (width = pageW/scale), which
    // reflows it shorter — so iterate measure→rescale to a stable fit
    // instead of measuring once at the wrong width.
    let scale = 1;
    if (scrollEl) {
      const prevWidth = scrollEl.style.width;
      for (let i = 0; i < 3; i++) {
        scrollEl.style.width = `${Math.round(pageW / scale) - PAD_X}px`;
        const fit = Math.min(1, Math.round((pageH / (scrollEl.scrollHeight + chrome)) * 1000) / 1000);
        if (Math.abs(fit - scale) < 0.002) {
          scale = Math.min(scale, fit);
          break;
        }
        scale = fit;
      }
      scrollEl.style.width = prevWidth;
      // Shave a hair so rounding can never push the bottom edge past the clamp.
      if (scale < 1) scale = Math.round(scale * 0.99 * 1000) / 1000;
    }
    const widthPct = Math.round(10000 / scale) / 100;
    // The scroll region fills whatever the page has left after the chrome,
    // pushing the disclaimer to the bottom edge (heights are pre-transform,
    // hence the divide by scale).
    const scrollMinH = Math.floor(pageH / scale - chrome);

    const pageStyle = document.createElement("style");
    pageStyle.textContent =
      `@page { margin: 0; size: letter ${sideBySide ? "landscape" : "portrait"}; } ` +
      `body.printing-story #story-print-area { transform: scale(${scale}) !important; width: ${widthPct}% !important; } ` +
      `body.printing-story #story-print-area .story-scroll { min-height: ${scrollMinH}px !important; }`;
    const cleanup = () => {
      document.body.classList.remove("printing-story", "printing-landscape");
      document.documentElement.classList.remove("printing-story", "printing-landscape");
      pageStyle.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    document.head.appendChild(pageStyle);
    // Class goes on BOTH <html> and <body>: html gets the one-page clamp,
    // body gets position:relative so the clamp contains the portal dialog.
    document.body.classList.add("printing-story");
    document.documentElement.classList.add("printing-story");
    if (sideBySide) {
      document.body.classList.add("printing-landscape");
      document.documentElement.classList.add("printing-landscape");
    }
    window.print();
  };

  if (!baseline || planCards.length === 0) return null;

  const storyScenario = storyId ? byId.get(storyId) : null;
  const storySteps = storyId ? stepsCache[storyId] : null;
  const inStory = !!(storyScenario && storySteps && storySteps.length > 0);
  const storyMax = inStory
    ? Math.max(baseline.results.totalTaxLiability, ...storySteps!.map((st) => st.taxThisYear))
    : story.maxTax;
  const storyFvEconDiff = storyScenario
    ? storyScenario.results.hpeFutureValue +
      storyScenario.results.estimatedFutureTaxAvoided -
      (storyScenario.results.totalEconomicCost - baseline.results.totalEconomicCost)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* top-[4vh] + translate-y-0 override the shared dialog’s translate-y-[-50%]:
          a sub-pixel compositor transform on this tall box smears the text. */}
      <DialogContent
        id="story-print-area"
        className={`flex h-[92vh] flex-col top-[4vh] translate-y-0 ${!inStory && !single ? "max-w-6xl" : "max-w-3xl"}`}
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-8">
            <div className="flex items-center gap-2">
              {inStory && !single && (
                <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setStoryId(null)}>
                  <ArrowLeft className="h-3.5 w-3.5" /> All strategies
                </Button>
              )}
              <DialogTitle>{baseline.input.name}</DialogTitle>
            </div>
            <Button variant="outline" size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={handlePrint}>
              <Printer className="h-3 w-3" />
              Print / Save PDF
            </Button>
          </div>
        </DialogHeader>

        <div className="story-scroll flex-1 overflow-y-auto pr-1">
          {loadingStory ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Building the story…</span>
            </div>
          ) : inStory ? (
            <>
              {/* STEP 1 — Where You Are */}
              <div className="rounded-2xl border bg-card p-5 shadow-sm">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                  Step 1 · Baseline
                </div>
                <h3 className="mt-0.5 text-lg font-bold">Where You Are</h3>
                <div className="mt-0.5 text-xs text-muted-foreground">Baseline — no strategy</div>
                <div className="mt-3 grid gap-4 sm:grid-cols-[240px_1fr] sm:items-center">
                  <div className="text-center">
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                      Starting AGI
                    </div>
                    <div className="mt-1 text-2xl font-extrabold tabular-nums">
                      {formatCurrency(baseline.results.adjustedGrossIncome)}
                    </div>
                  </div>
                  <TaxPanel card={baseCard} maxTax={storyMax} />
                </div>
                <StatLine card={baseCard} />
              </div>

              {storySteps!.map((step) => (
                <div key={step.stepNo}>
                  <div className="flex justify-center py-1.5">
                    <ArrowDown className="h-5 w-5 text-muted-foreground/50" />
                  </div>
                  <div className="rounded-2xl border bg-card p-5 shadow-sm">
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-primary">
                      Step {step.stepNo}
                    </div>
                    <h3 className="mt-0.5 text-lg font-bold">{step.title}</h3>
                    <div className="mt-0.5 text-xs text-muted-foreground">{step.recap}</div>
                    <div className="mt-3 grid gap-4 sm:grid-cols-[240px_1fr] sm:items-center">
                      <MovePill {...step.move} />
                      <div className="rounded-xl border bg-muted/30 p-4">
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                          Tax this year
                        </div>
                        <div className="mt-1.5 flex items-center gap-3">
                          <div
                            className={`h-4 rounded ${step.deltaVsPrev < 0 ? "bg-blue-500" : "bg-slate-700"}`}
                            style={{ width: `${Math.max(6, Math.round((step.taxThisYear / storyMax) * 100))}%`, minWidth: 8 }}
                          />
                          <div className="whitespace-nowrap font-extrabold tabular-nums">
                            {formatCurrency(step.taxThisYear)}
                          </div>
                        </div>
                        {Math.abs(step.deltaVsPrev) >= 0.5 && (
                          <div className={`mt-1.5 text-xs font-bold tabular-nums ${step.deltaVsPrev > 0 ? "text-amber-700" : "text-success"}`}>
                            {step.deltaVsPrev > 0 ? "+" : "−"}
                            {formatCurrency(Math.abs(step.deltaVsPrev))}
                          </div>
                        )}
                        {step.notes.length > 0 && (
                          <div className="mt-2 space-y-1 border-t border-dashed pt-2 text-xs text-muted-foreground">
                            {step.notes.map((n) => (
                              <p key={n}>{n}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap justify-center gap-6 border-t border-dashed pt-3 text-center">
                      <div>
                        <div className="text-sm font-extrabold tabular-nums">{formatCurrency(step.taxableIncome)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">taxable income</div>
                      </div>
                      <div>
                        <div className="text-sm font-extrabold tabular-nums">{formatPercent(step.effectiveRate)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">effective rate</div>
                      </div>
                      <div>
                        <div className="text-sm font-extrabold tabular-nums">{formatCurrency(step.deductionUsed)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">deduction used</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <PayoffBox fvEconDiff={storyFvEconDiff} scenario={storyScenario!} growthYears={story.growthYears} />
              <p className="mt-2 text-center text-[10px] text-muted-foreground">
                Engine-computed · {storySteps![storySteps!.length - 1].recap}
              </p>
            </>
          ) : (
            <>
              {/* Baseline — one shared "Where You Are" card on top. */}
              <div className="rounded-2xl border bg-card p-5 shadow-sm">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                  Baseline
                </div>
                <h3 className="mt-0.5 text-lg font-bold">Where You Are</h3>
                <div className="mt-3 grid gap-4 sm:grid-cols-[240px_1fr] sm:items-center">
                  <div className="text-center">
                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                      No strategy applied
                    </div>
                    <div className="mt-1 text-2xl font-extrabold tabular-nums">
                      {formatCurrency(baseCard.taxableIncome)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">taxable income</div>
                  </div>
                  <TaxPanel card={baseCard} maxTax={story.maxTax} />
                </div>
              </div>

              <div className="flex justify-center py-1.5">
                <ArrowDown className="h-5 w-5 text-muted-foreground/50" />
              </div>

              {/* One summary card per chosen strategy, side by side, each with
                  an expand control into its step story. */}
              <div className={single ? "" : `story-compare-grid grid gap-3 ${planCards.length === 2 ? "story-cols-2 grid-cols-1 md:grid-cols-2" : "story-cols-3 grid-cols-1 md:grid-cols-3"}`}>
                {planCards.map((card) => {
                  const scenario = byId.get(card.scenarioId);
                  if (!scenario) return null;
                  return (
                    <div key={card.scenarioId} className="rounded-2xl border bg-card p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-[10px] font-extrabold uppercase tracking-widest text-primary">
                            Strategy
                          </div>
                          <h3 className="mt-0.5 text-lg font-bold">{card.title}</h3>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1 px-2 text-xs"
                          onClick={() => openStory(card.scenarioId)}
                          title="Open this plan’s step-by-step story"
                        >
                          <Maximize2 className="h-3.5 w-3.5" /> Story
                        </Button>
                      </div>
                      <div className={`mt-3 ${single ? "grid gap-4 sm:grid-cols-[240px_1fr] sm:items-center" : "space-y-3"}`}>
                        <div className="space-y-2">
                          {card.moves.map((m) => (
                            <MovePill key={m.label} {...m} />
                          ))}
                        </div>
                        <TaxPanel card={card} maxTax={story.maxTax} />
                      </div>
                      <StatLine card={card} />
                      <PayoffBox fvEconDiff={card.fvEconDiff} scenario={scenario} growthYears={story.growthYears} />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <p className="border-t pt-2 text-[10px] italic leading-snug text-muted-foreground">{CYA_TEXT}</p>
      </DialogContent>
    </Dialog>
  );
}
